import React, { useState, useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { shallowEqual, useSelector } from 'react-redux';
import imageUrlBuilder from '@sanity/image-url';

import useView from '@tmap/mmm-core/redux/views/useView';
import { fromKey, toKey, types } from '@tmap/mmm-core/entityKey';
import miniHandlebars from '@tmap/mmm-core/miniHandlebars';
import locationLabelText from '@tmap/mmm-core/locationLabelText';
import isEmptyish from '@tmap/mmm-core/isEmptyish';
import useDocumentCache from '@tmap/mmm-core/redux/documentCache/useDocumentCache';
import { topics } from '@tmap/mmm-core/contributionTopics';

import { Alert } from '@tmap/mmm-style-guide/src/Alert';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Card } from '@tmap/mmm-style-guide/src/Card';
import { ThemeProvider, useTheme } from '@tmap/mmm-style-guide/src/Theming';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { ArrowForward, MapsUgcOutlined, NotListedLocationOutlined } from '@tmap/mmm-style-guide/src/Icon';
import { MenuItem } from '@tmap/mmm-style-guide/src/MenuItem';
import { Select } from '@tmap/mmm-style-guide/src/Select';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Skeleton } from '@tmap/mmm-style-guide/src/Skeleton';
import useLazySSR from '@tmap/mmm-style-guide/src/ssr/useLazySSR';

import client from '../../../client';
import FullWrapper from '../../../layouts/fullWrapper';
import ColorBlocks from '../../../layouts/colorBlocks';
import TwoTone from '../../../layouts/twoTone';
import Metatags from '../../../components/metatags';
import {
  getPaginatedCommunityContributions,
  getPaginatedCommunityContributionsView,
  getPendingContributions,
  getTopicTotals,
} from '../../../actions/contributions';
import { DARK_THEME } from '../../../lib/themes';
import sentenceCase from '../../../lib/sentenceCase';
import getPromotedIncentives from '../../../lib/incentive/getPromotedIncentives';
import IncentiveCard from '../../../components/incentiveCard/horizontal';
import SimilarOffersCard from '../../../components/communityContent/similarOffersCard';
import { getRandomEntry } from '../../../lib/helpers';
import SurveyQuestionBlock from '../../../components/survey/surveyQuestionBlock';
import InfiniteScroll from '../../../components/infiniteScroll';
import PostGridItems from '../../../components/communityContent/postGridItems';
import QuestionGridItems from '../../../components/communityContent/questionGridItems';
import isUserModerator from '../../../lib/community/isUserModerator';
import ModeratorBanner from '../../../components/communityContent/moderatorBanner';
import PendingCTA from '../../../components/communityContent/pendingCTA';
import CommunityContext from '../../../lib/community/communityContext';
import CommunityControls from '../../../components/communityControls';
import { CONTRIBUTIONS_CACHE } from '../../../actions/cacheIndex';
import OfferProgressContext from '../../../components/incentiveCard/offerProgressContext';

const builder = imageUrlBuilder(client);

const BreadcrumbWrapper = styled(Box)(({ theme }) => ({
  color: theme.palette.text.primary,
  paddingTop: theme.spacing(2),
}));

const CommunityNameHeader = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  color: theme.palette.green.main,
  marginBottom: 0,
}));

const CommunityNameGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(5.25),
  marginBottom: theme.spacing(4.5),
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
}));

const TopicHeader = styled(Typography)(({ theme }) => ({
  fontSize: '42px',
  marginBottom: 0,
  overflowX: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
  },
}));

const Subheader = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: '1.5em',
  marginBottom: '11px',
}));

const QuestionCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  padding: theme.spacing(3.5, 2.5),
  borderRadius: '8px',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(6, 2.5),
  },
}));

const StyledCardIcon = styled(MapsUgcOutlined)(({ theme }) => ({
  color: theme.palette.green.light,
  [theme.breakpoints.down('md')]: {
    fontSize: '36px',
  },
}));

const CardCTA = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  fontWeight: 800,
  [theme.breakpoints.down('md')]: {
    fontSize: '20px',
    display: 'block',
    textAlign: 'center',
    marginTop: theme.spacing(1),
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '16px',
  },
}));

const CardText = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  fontSize: '16px',
  color: theme.palette.primary.contrastText,
  [theme.breakpoints.down('md')]: {
    display: 'block',
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },
  [theme.breakpoints.up('md')]: {
    marginLeft: theme.spacing(1),
  },
}));

const QuestionButton = styled(Button)(({ theme }) => ({
  width: '100%',
  '& .MuiButton-root': {
    '&:hover': {
      background: theme.palette.green.dark,
    },
    color: theme.palette.text.primary,
    padding: theme.spacing(0.75, 4),
    backgroundColor: theme.palette.green.light,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
}));

const FlexGridItem = styled(Grid)(() => ({
  display: 'flex',
  justifyContent: 'center',
}));

const NewPostButton = styled(Button)(({ theme }) => ({
  '& .MuiButton-root': {
    padding: theme.spacing('7.5px', 2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
      width: '100%',
    },
  },
}));

const SortSelect = styled(Select)(({ theme }) => ({
  fontSize: '14px',
  height: '40px',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    textAlign: 'center',
  },
}));

const MissingOffersCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: '8px',
  padding: theme.spacing(6, 4),
}));

const isCommunityCustomer = (community) => community.offers?.some((offer) => (
  !isEmptyish(offer.offerId)
));

const generateDefaultTopicSettings = (topic, locationLabel, isHashtag) => ({
  breadcrumb: isHashtag ? `#${topic}` : sentenceCase(topic),
  header: isHashtag ? `#${topic} in ${locationLabel}` : `${sentenceCase(topic)} in ${locationLabel}`,
  buttonText: 'Submit a Post',
  singular: 'Post',
  plural: 'Posts',
});

function CommunityTopicPage(props) {
  const {
    initialContributions,
    community,
    pageSettings,
    topicId,
    suggestedTopics,
    otherOffers,
    topicSettings,
    offerProgressSettings,
  } = props;
  const router = useRouter();
  const theme = useTheme();
  const [sortBy, setSortBy] = useState('top');
  const [page, setPage] = useState(0);
  const handleSort = useCallback((e) => {
    setSortBy(e.target.value);
    setPage(0);
  }, []);
  const handleNewPage = useCallback(() => setPage(page + 1), [page]);
  // Show a loading skeleton for SSR
  const testimonial = useLazySSR((ssr) => (
    ssr
      ? null
      : getRandomEntry(community.testimonials)
  ), [community.testimonials]);
  // Show the offer card empty state for SSR
  const offerToShow = useLazySSR((ssr) => (
    ssr
      ? null
      : getRandomEntry(community.offers)
  ), [community.offers]);

  const { topic: topicSlug, page: pageParam = 1 } = router.query;
  const canonicalUrl = `${process.env.NEXT_PUBLIC_CANONICAL_DOMAIN}/moving-living/${community.slug.current}/${topicSlug}`;
  const initialPage = Math.max(+pageParam, 1);
  const numberOfFeaturedPosts = initialContributions?.paging?.limit;

  // The first page has variable length, subsequent pages have 6
  const limit = page === 0 ? numberOfFeaturedPosts : 6;

  // The page query param is for indexing, page lengths always match initial set for that one
  const initialOffset = numberOfFeaturedPosts * (initialPage - 1);
  const offset = initialOffset + (page >= 1 ? numberOfFeaturedPosts + ((page - 1) * 6) : 0);

  const contributionFilters = useMemo(
    () => {
      const filters = { communityId: community._id };
      if (Object.keys(topics).includes(topicId)) {
        filters.topics = topicId;
      } else if (topicId.toLowerCase() === 'questions') {
        filters.type = 'question';
      } else {
        filters.hashtags = `#${topicId.toLowerCase()}`;
      }
      return filters;
    },
    [community._id, topicId],
  );
  const [contributionIds, view] = useView(['topicContributions', community._id], getPaginatedCommunityContributionsView(limit, offset, sortBy, contributionFilters), { page, squashPages: true });

  const contributions = useDocumentCache(CONTRIBUTIONS_CACHE, contributionIds)
    .filter((contrib) => !contrib.deleted);

  const upperContributions = (contributions?.length > 0
    ? contributions.slice(0, numberOfFeaturedPosts)
    : initialContributions.docs)
    .filter((contrib) => !contrib.deleted);

  const locationText = locationLabelText(community.locationText, community.regionText);
  const topicDetails = community.topicSettings ?? topicSettings;
  const {
    breadcrumb = sentenceCase(topicId),
    header = `${sentenceCase(topicId)} in ${locationText}`,
    buttonText = 'Submit a Post',
    plural = 'Posts',
    singular = 'Post',
    longDescription,
  } = topicDetails;

  const handlebarsData = {
    ...community,
    locationText,
  };
  const title = miniHandlebars(header, handlebarsData);

  const scrollingContributions = contributions.slice(numberOfFeaturedPosts);

  const showHowItWorksLongText = isCommunityCustomer(community) && pageSettings.howItWorks;
  const showOtherOffers = !isCommunityCustomer(community) && otherOffers?.length > 0;
  const howItWorksHeader = (
    <Typography variant='body1' component='h3' sx={{ marginBottom: '8px' }}>
      <strong>Get Paid to Live</strong> in {locationText}
    </Typography>
  );

  // only allow the user to create new posts if
  // 1. they're not blocked
  // 2. they don't have any pending posts OR they're already preapproved
  const userId = useSelector((state) => state?.users?.current?.userId, shallowEqual);
  const userRoles = useSelector((state) => state?.users?.current?.roles, shallowEqual);
  const pendingContributionFilters = useMemo(() => ({
    communityId: community._id,
    $or: [{ type: 'default' }, { type: null }],
  }), [community._id]);
  const [pendingContributionIds] = useView(['contributions', community._id], getPendingContributions(
    userId,
    pendingContributionFilters,
  ));
  const communityRole = (userRoles ?? []).find((role) => fromKey(role).id === community._id);
  const userIsBlocked = communityRole === toKey(types.BLOCKED, community._id);
  const preapproved = !userIsBlocked && (userRoles?.includes('employee') || Boolean(communityRole));
  const showPostButton = preapproved || isEmptyish(pendingContributionIds);

  const userIsModerator = isUserModerator({ roles: userRoles }, community._id);

  const isQuestionPage = topicId.toLowerCase() === 'questions';

  const postUrl = isQuestionPage
    ? `/moving-living/${community.slug.current}/post?returnUrl=${router.asPath}&type=question`
    : `/moving-living/${community.slug.current}/post?returnUrl=${router.asPath}`;
  const handleOpen = () => router.push(postUrl);

  const breadcrumbs = [
    { url: '/moving-living', text: 'Communities', breakpoint: 'sm' },
    { url: `/moving-living/${community.slug.current}`, text: locationText, breakpoint: 'sm' },
    { text: breadcrumb },
  ];

  const handleUpperContributionsDisplay = () => (
    isQuestionPage ? (
      <QuestionGridItems
        posts={upperContributions}
        pageId='communityTopic'
      />
    ) : (
      <PostGridItems
        posts={upperContributions}
        testimonials={testimonial ? [testimonial] : undefined}
        suggestedTopics={suggestedTopics}
        community={community}
        onPostClick={showPostButton ? handleOpen : null}
        pageId='communityTopic'
      />
    )
  );

  return (
    <OfferProgressContext.Provider value={offerProgressSettings}>
      <CommunityContext.Provider value={community}>
        <Metatags
          title={`${sentenceCase(topicId)} in ${locationText}`}
          canonical={canonicalUrl}
          next={initialContributions.paging.hasNextPage ? `${canonicalUrl}?page=${initialPage + 1}` : undefined}
          prev={initialContributions.paging.hasPrevPage ? `${canonicalUrl}?page=${initialPage - 1}` : undefined}
          {...(community.galleryImages?.length > 0
            ? { image: builder.image(community.galleryImages[0]).url() } : {})}
        />
        <FullWrapper>
          {userIsModerator && <ModeratorBanner community={community} />}
          <TwoTone cutoff='0%' primary={theme.palette.primary.dark}>
            <ThemeProvider theme={DARK_THEME}>
              <BreadcrumbWrapper color='textPrimary'>
                <ColorBlocks.Block spacing='small'>
                  <Grid container alignItems='center'>
                    <CommunityControls canonicalUrl={canonicalUrl} breadcrumbs={breadcrumbs} />
                    <CommunityNameGrid item xs={12}>
                      <CommunityNameHeader gutterBottom>
                        {locationText}
                      </CommunityNameHeader>
                      <TopicHeader component='h1' fontWeight={700}>
                        {title}
                      </TopicHeader>
                    </CommunityNameGrid>
                  </Grid>
                </ColorBlocks.Block>
              </BreadcrumbWrapper>
            </ThemeProvider>
          </TwoTone>
          {longDescription && (
            <ColorBlocks.Block>
              <Subheader component='h2'>
                {miniHandlebars(pageSettings.topicPageSubHeader, handlebarsData)}
              </Subheader>
              <Typography variant='body1' sx={{ whiteSpace: 'pre-line' }}>
                {longDescription}
              </Typography>
            </ColorBlocks.Block>
          )}
          <ColorBlocks.Block>
            <Grid container spacing={4} sx={{ marginTop: longDescription ? 0 : '-48px' }}>
              {userIsModerator && pendingContributionIds.length > 0
                ? <PendingCTA numPending={pendingContributionIds.length} /> : null}
              {
                !userIsModerator && pendingContributionIds.length > 0 ? (
                  <Grid item xs={12}>
                    <Alert severity='info'>You have one or more posts pending community review. They will be viewable once they are approved by a community admin.</Alert>
                  </Grid>
                ) : null
              }
              <Grid item xs sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                <strong>
                  {view.paging?.totalDocs || sentenceCase(sortBy)}&nbsp;
                  {view.paging?.totalDocs === 1 ? singular : plural}
                </strong>
                &nbsp;in {locationText}
              </Grid>
              <Grid item container spacing={2} xs={12} sm='auto'>
                {showPostButton ? (
                  <Grid item xs={6} sm sx={{ textAlign: 'left' }}>
                    <NewPostButton id='post' variant='contained' color='inverted' onClick={handleOpen} disableElevation>
                      {buttonText}
                    </NewPostButton>
                  </Grid>
                ) : null}
                <Grid item xs={6} sm='auto'>
                  <SortSelect
                    id='post-sort'
                    value={sortBy}
                    onChange={handleSort}
                    defaultValue='top'
                    size='small'
                  >
                    <MenuItem value='top'>Most Popular</MenuItem>
                    <MenuItem value='new'>Most Recent</MenuItem>
                  </SortSelect>
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2}>
                {upperContributions.length > 0
                  ? handleUpperContributionsDisplay()
                  : (
                    <Grid item>
                      <Typography>
                        Oops! It looks like this community doesn&apos;t have any posts here yet.
                      </Typography>
                    </Grid>
                  )}
              </Grid>
              {isQuestionPage && (
                <Grid item xs={12}>
                  <QuestionCard>
                    <Grid container alignItems='center' justifyContent='center' spacing={1}>
                      <FlexGridItem item xs={12} md='auto'>
                        <StyledCardIcon />
                      </FlexGridItem>
                      <Grid item xs={12} md>
                        <CardCTA variant='body1' component='span'>
                          Can&apos;t quite find what you&apos;re looking for?
                        </CardCTA>
                        <CardText variant='body1' component='span'>
                          No worries, take the lead by posting a new question!
                        </CardText>
                      </Grid>
                      <FlexGridItem item xs={12} md='auto'>
                        <QuestionButton variant='contained' onClick={handleOpen} disableElevation>
                          Ask a question
                        </QuestionButton>
                      </FlexGridItem>
                    </Grid>
                  </QuestionCard>
                </Grid>
              )}
            </Grid>
          </ColorBlocks.Block>
          <ColorBlocks.Block spacing='none'>
            <Grid container spacing={2}>
              {!showHowItWorksLongText && (
                <Grid item xs={12}>
                  {howItWorksHeader}
                </Grid>
              )}
              {showHowItWorksLongText && (
                <Grid item xs={12} md={4}>
                  <Box sx={{ padding: '24px 0' }}>
                    {howItWorksHeader}
                    <Typography variant='body2'>
                      {miniHandlebars(pageSettings.howItWorks, community)}
                    </Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} md={showHowItWorksLongText || showOtherOffers ? 8 : 12}>
                {offerToShow ? (
                  <IncentiveCard incentive={offerToShow} placementId={`communityTopic-${topicSlug}-featuredIncentive`} pageId='communityTopic' />
                ) : (
                  <MissingOffersCard>
                    <Grid container direction='column' alignItems='center' justifyContent='center' spacing={1} minHeight='80%'>
                      <Grid item>
                        <NotListedLocationOutlined sx={{ fontSize: '48px', color: (t) => t.palette.secondary.main }} />
                      </Grid>
                      <Grid item>
                        <Typography textAlign='center' fontSize='20px' fontWeight={700}>
                          {locationText} doesn&apos;t have any offers right now.
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant='body1' textAlign='center'>
                          This may change in the future, so check back regularly!
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Button size='large' variant='outlined' href='/moving-living?relocation=true' endIcon={<ArrowForward />} sx={{ marginTop: 1 }}>
                          Communities with Programs
                        </Button>
                      </Grid>
                    </Grid>

                  </MissingOffersCard>
                )}
              </Grid>
              {showOtherOffers && (
                <Grid item xs={12} md={4}>
                  <SimilarOffersCard offers={otherOffers} headerText={!offerToShow && 'Other Programs'} />
                </Grid>
              )}
            </Grid>
          </ColorBlocks.Block>
          <SurveyQuestionBlock community={community} />
          <ColorBlocks.Block>
            <InfiniteScroll
              shouldLoadNewPage={!view.isLoading && view.paging?.hasNextPage}
              handleNewPage={handleNewPage}
            >
              <Box>
                <Grid container spacing={2}>
                  {isQuestionPage ? (
                    <QuestionGridItems
                      posts={scrollingContributions}
                      pageId='communityTopic'
                    />
                  ) : (
                    <PostGridItems
                      posts={scrollingContributions}
                      community={community}
                      pageId='communityTopic'
                    />
                  )}
                </Grid>
                {view.isLoading && (
                  <Grid container spacing={2} sx={{ marginTop: 2, marginBottom: '744px' }}>
                    {[...Array(6)].map((x, i) => (
                      <Grid item xs={12} sm={6} md={4} key={`post-skeleton-${i}`} alignItems='flex-start'>
                        <Skeleton variant='rectangular' width='100%' height={220} />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </InfiniteScroll>
          </ColorBlocks.Block>
        </FullWrapper>
      </CommunityContext.Provider>
    </OfferProgressContext.Provider>
  );
}

export const getServerSideProps = async (context) => {
  const { topic, slug = '', page = 1 } = context.query;
  const topicId = topic.toUpperCase();
  if (!topic) {
    return { notFound: true };
  }
  // Show up to 6 posts, but one less each for suggested topics and/or testimonials if present
  let numberOfInitialPosts = 6;

  const { community, pageSettings, topicSettings, offerProgressSettings } = await client.fetch(`{
    'community': *[_type == 'community' && slug.current == $slug][0]{
      ...,
      'offers': *[ _type == 'incentive' && status != 'inactive' && ^._id in communities[]._ref ]{..., communities[]->{regionText}},
      'topicSettings': *[ _type == 'topicDetails' && slug == $topic && ^._id == community._ref][0]
    },
    'pageSettings': *[_id == 'communitySettings'][0],
    'topicSettings': *[_type == 'topicDetails' && slug == $topic && community == null][0],
    'offerProgressSettings': *[_id == 'offerProgressSettings'][0],
  }`, { slug, topic: topic.toLowerCase() });
  if (!community) {
    return { notFound: true };
  }
  if (community.testimonials?.length > 0) {
    numberOfInitialPosts--;
  }

  const otherQueries = [getTopicTotals(community._id)];
  const shouldFetchOtherOffers = !isCommunityCustomer(community);

  if (shouldFetchOtherOffers) {
    otherQueries.push(getPromotedIncentives(3, community.regionZone.toLowerCase()));
  }
  const results = await Promise.all(otherQueries);

  // show suggested topics if at least 4 other topics have 4 or more posts each
  const suggestedTopics = (results[0]?.data?.filter((t) => t.count >= 4));
  const showSuggestedTopics = suggestedTopics.length >= 4;
  if (showSuggestedTopics) {
    numberOfInitialPosts--;
  }

  const contributionFilters = { communityId: community._id };
  let isHashtag = false;
  if (Object.keys(topics).includes(topic.toUpperCase())) {
    contributionFilters.topics = topicId;
  } else if (topic.toLowerCase() === 'questions') {
    contributionFilters.type = 'question';
  } else {
    contributionFilters.hashtags = `#${topic}`;
    isHashtag = true;
  }

  const initialContributions = await getPaginatedCommunityContributions(
    numberOfInitialPosts,
    numberOfInitialPosts * Math.max(page - 1, 0),
    'top',
    contributionFilters,
  );
  const analyticsProps = {
    pageName: 'community topic',
    entityKey: toKey(types.COMMUNNITY, community._id),
  };
  const locationLabel = locationLabelText(community.locationText, community.regionText);
  return {
    props: {
      analyticsProps,
      topicId,
      community: {
        ...community,
        formattedLocationText: locationLabel,
      },
      pageSettings,
      initialContributions,
      suggestedTopics: showSuggestedTopics ? suggestedTopics : null,
      otherOffers: shouldFetchOtherOffers ? results[1] : null,
      topicSettings: topicSettings ?? generateDefaultTopicSettings(topic, locationLabel, isHashtag),
      offerProgressSettings,
    },
  };
};

export default CommunityTopicPage;
